// Central SEO configuration for Zebaa.
// Update REACT_APP_SITE_URL in frontend/.env to your live domain before deploying.
export const SITE_URL = (process.env.REACT_APP_SITE_URL || "https://www.zebaa.uk").replace(/\/$/, "");

export const BUSINESS = {
  name: "Zebaa",
  legalName: "Zebaa LTD",
  description:
    "Zebaa is a South Asian imitation jewellery shop on Green Street, East London - specialising in bridal sets, kundan-style necklaces, gold-tone bangles, earrings and hand-embellished bridal jewellery.",
  streetAddress: "Unit 1, 312 Green Street",
  addressLocality: "London",
  addressRegion: "Greater London",
  postalCode: "E13 9AP",
  addressCountry: "GB",
  areaServed: ["Green Street", "Upton Park", "East Ham", "Forest Gate", "Plaistow", "Manor Park", "Stratford", "Ilford", "Barking", "Newham", "East London", "London"],
  email: "hello@zebaa.uk",
  latitude: 51.5364514,
  longitude: 0.0350801,
  priceRange: "££",
  instagram: "https://www.instagram.com/zebaa.uk",
  googleMaps: "https://www.google.com/maps/place/Zebaa/@51.5364514,0.0350801,17z",
  image: `${SITE_URL}/og-image.jpg`,
  logo: `${SITE_URL}/logo512.png`,
  openingHours: [
    { days: ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"], opens: "10:00", closes: "19:00" },
    { days: ["Sunday"], opens: "11:00", closes: "17:00" },
  ],
};

export const DEFAULT_TITLE =
  "Zebaa | South Asian Bridal & Imitation Jewellery - Green Street, London";
export const DEFAULT_DESCRIPTION =
  "Zebaa on Green Street, East London (E13 9AP) - a South Asian imitation jewellery shop. Bridal sets, kundan-style necklaces, gold-tone bangles, earrings & chandbalis, in-store and online.";

export const abs = (path = "/") => `${SITE_URL}${path.startsWith("/") ? path : `/${path}`}`;

// ---- Structured data builders (JSON-LD) ----
export const localBusinessSchema = () => ({
  "@context": "https://schema.org",
  "@type": "JewelryStore",
  "@id": `${SITE_URL}/#store`,
  name: BUSINESS.name,
  description: BUSINESS.description,
  url: SITE_URL,
  image: BUSINESS.image,
  logo: BUSINESS.logo,
  email: BUSINESS.email,
  priceRange: BUSINESS.priceRange,
  currenciesAccepted: "GBP",
  paymentAccepted: "Cash, Credit Card, Debit Card",
  address: {
    "@type": "PostalAddress",
    streetAddress: BUSINESS.streetAddress,
    addressLocality: BUSINESS.addressLocality,
    addressRegion: BUSINESS.addressRegion,
    postalCode: BUSINESS.postalCode,
    addressCountry: BUSINESS.addressCountry,
  },
  geo: { "@type": "GeoCoordinates", latitude: BUSINESS.latitude, longitude: BUSINESS.longitude },
  hasMap: `https://www.google.com/maps?q=${encodeURIComponent("Unit 1, 312 Green Street, London, E13 9AP")}`,
  areaServed: BUSINESS.areaServed.map((a) => ({ "@type": "Place", name: a })),
  openingHoursSpecification: BUSINESS.openingHours.map((h) => ({
    "@type": "OpeningHoursSpecification",
    dayOfWeek: h.days,
    opens: h.opens,
    closes: h.closes,
  })),
  sameAs: [BUSINESS.instagram, BUSINESS.googleMaps],
  keywords:
    "Zebaa, Zebaa London, Zebaa Green Street, imitation jewellery shop Green Street, South Asian bridal jewellery London, kundan-style necklace London, bridal sets East London, gold-tone bangles Newham, Indian jewellery shop East Ham, wedding jewellery Upton Park, chandbalis London, jhumkas East London, Asian jewellery boutique Ilford",
});

export const organizationSchema = () => ({
  "@context": "https://schema.org",
  "@type": "Organization",
  "@id": `${SITE_URL}/#organization`,
  name: BUSINESS.name,
  url: SITE_URL,
  logo: BUSINESS.logo,
  email: BUSINESS.email,
  sameAs: [BUSINESS.instagram, BUSINESS.googleMaps],
});

export const websiteSchema = () => ({
  "@context": "https://schema.org",
  "@type": "WebSite",
  "@id": `${SITE_URL}/#website`,
  name: BUSINESS.name,
  url: SITE_URL,
  publisher: { "@id": `${SITE_URL}/#organization` },
  potentialAction: {
    "@type": "SearchAction",
    target: { "@type": "EntryPoint", urlTemplate: `${SITE_URL}/shop/all?q={search_term_string}` },
    "query-input": "required name=search_term_string",
  },
});

export const breadcrumbSchema = (items) => ({
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  itemListElement: items.map((it, i) => ({
    "@type": "ListItem",
    position: i + 1,
    name: it.name,
    item: abs(it.path),
  })),
});

export const productSchema = (product) => ({
  "@context": "https://schema.org",
  "@type": "Product",
  name: product.name,
  image: product.image,
  description: product.description || `${product.name} - ${product.subtitle || "South Asian imitation jewellery"} at Zebaa, Green Street London.`,
  sku: product.id,
  brand: { "@type": "Brand", name: BUSINESS.name },
  category: (product.category || "").replace(/-/g, " "),
  offers: {
    "@type": "Offer",
    priceCurrency: "GBP",
    price: product.price,
    priceValidUntil: `${new Date().getFullYear() + 1}-12-31`,
    itemCondition: "https://schema.org/NewCondition",
    availability: product.stock > 0 ? "https://schema.org/InStock" : "https://schema.org/OutOfStock",
    url: abs(`/product/${product.slug}`),
    seller: { "@id": `${SITE_URL}/#store` },
    shippingDetails: {
      "@type": "OfferShippingDetails",
      // UK delivery is free on orders over £40 (see FreeDeliveryBar.jsx), otherwise £4.99.
      shippingRate: { "@type": "MonetaryAmount", value: Number(product.price) >= 40 ? "0" : "4.99", currency: "GBP" },
      shippingDestination: { "@type": "DefinedRegion", addressCountry: "GB" },
      deliveryTime: {
        "@type": "ShippingDeliveryTime",
        handlingTime: { "@type": "QuantitativeValue", minValue: 0, maxValue: 1, unitCode: "DAY" },
        transitTime: { "@type": "QuantitativeValue", minValue: 2, maxValue: 3, unitCode: "DAY" },
      },
    },
    hasMerchantReturnPolicy: {
      "@type": "MerchantReturnPolicy",
      applicableCountry: "GB",
      returnPolicyCategory: "https://schema.org/MerchantReturnFiniteReturnWindow",
      merchantReturnDays: 14,
      returnMethod: "https://schema.org/ReturnByMail",
      returnFees: "https://schema.org/ReturnShippingFees",
    },
  },
});

export const faqSchema = (faqs) => ({
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: faqs.map((f) => ({
    "@type": "Question",
    name: f.q,
    acceptedAnswer: { "@type": "Answer", text: f.a },
  })),
});

// Category listing page: CollectionPage + ItemList of products (helps Google
// build rich category results for queries like "sarees", "lehengas").
export const collectionPageSchema = ({ name, description, path, products = [] }) => ({
  "@context": "https://schema.org",
  "@type": "CollectionPage",
  "@id": `${abs(path)}#collection`,
  name,
  description,
  url: abs(path),
  isPartOf: { "@id": `${SITE_URL}/#website` },
  about: { "@id": `${SITE_URL}/#store` },
  mainEntity: {
    "@type": "ItemList",
    numberOfItems: products.length,
    itemListElement: products.slice(0, 24).map((p, i) => ({
      "@type": "ListItem",
      position: i + 1,
      url: abs(`/product/${p.slug}`),
      name: p.name,
    })),
  },
});

